import type { DocumentTableRow } from 'features/knowledgeDocumentViewer'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { useSystemDocumentByBqId } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import {
  getDocChatPreview,
  resolveClickedPreviewView,
  searchStateWithDocChatPreview,
} from 'features/knowledgeDocumentViewer/utils/docChatPreview'
import { useWorkOrder } from 'features/knowledgeDocumentViewer/workOrders/useWorkOrder'
import {
  isCmmsRecordId,
  workOrderRowFromEntry,
} from 'features/knowledgeDocumentViewer/workOrders/workOrder'
import { useCallback, useMemo, useState } from 'react'

export function useAgenticChatPreview(): {
  handleClosePreview: () => void
  handleDismissCitedQuotes: () => void
  handlePreviewDocument: (
    document: DocumentTableRow,
    pageNumber?: number,
    citedQuotes?: string[]
  ) => void
  isLoadingPreview: boolean
  isPreviewOpen: boolean
  previewCitedQuotes: string[] | undefined
  previewDocument: DocumentTableRow | null
  previewPage: number
} {
  const { onSearchStateChange, searchState } = useAdvancedSearchUX()
  const preview = getDocChatPreview(searchState.docChat)
  const [clickedPreview, setClickedPreview] = useState<{
    document: DocumentTableRow
    page: number
    citedQuotes?: string[]
  } | null>(null)
  const { document: fetchedDocument, isLoading } = useSystemDocumentByBqId(
    isCmmsRecordId(preview?.previewDocumentId)
      ? undefined
      : preview?.previewDocumentId
  )

  const { entry: workOrder, isLoading: isWorkOrderLoading } = useWorkOrder(
    preview?.previewDocumentId
  )
  const previewDocument = useMemo(() => {
    if (clickedPreview) return clickedPreview.document
    if (workOrder) return workOrderRowFromEntry(workOrder)
    if (!fetchedDocument) return null
    return transformSystemDocumentToTableRow(fetchedDocument)
  }, [clickedPreview, fetchedDocument, workOrder])

  const handlePreviewDocument = useCallback(
    (document: DocumentTableRow, pageNumber = 1, citedQuotes?: string[]) => {
      setClickedPreview({ document, page: pageNumber, citedQuotes })
      onSearchStateChange((currentSearchState) =>
        searchStateWithDocChatPreview(currentSearchState, {
          previewDocumentId: document.id,
          previewPage: pageNumber,
        })
      )
    },
    [onSearchStateChange]
  )

  const handleDismissCitedQuotes = useCallback(() => {
    setClickedPreview((current) =>
      current ? { ...current, citedQuotes: undefined } : current
    )
  }, [])

  const handleClosePreview = useCallback(() => {
    setClickedPreview(null)
    onSearchStateChange((currentSearchState) =>
      searchStateWithDocChatPreview(currentSearchState, null)
    )
  }, [onSearchStateChange])

  const { previewPage, previewCitedQuotes } = resolveClickedPreviewView(
    clickedPreview,
    preview
  )

  return {
    handleClosePreview,
    handleDismissCitedQuotes,
    handlePreviewDocument,
    isLoadingPreview:
      preview !== undefined &&
      (isLoading || isWorkOrderLoading) &&
      previewDocument === null,
    isPreviewOpen:
      clickedPreview !== null ||
      previewDocument !== null ||
      (preview !== undefined && (isLoading || isWorkOrderLoading)),
    previewCitedQuotes,
    previewDocument,
    previewPage,
  }
}
